/**
 * Asking to be reminded, and arranging for it.
 *
 * Permission is requested at the moment someone asks for reminders, never on load. A
 * permission prompt nobody invited is the fastest way to a permanent "block", and a blocked
 * notification permission cannot be asked for again — it has to be undone in browser settings,
 * which nobody does.
 *
 * On iOS none of this works at all until the app is on the Home Screen, which is why the
 * install invitation says so.
 */

import type { ApiClient } from '../api/client';
import { VAPID_PUBLIC_KEY } from '../config';

export type ReminderState =
  /** This browser cannot do reminders, or the app is not installed on iOS. */
  | 'unsupported'
  /** Permission was refused. Nothing here can ask again. */
  | 'blocked'
  /** Available, and not turned on. */
  | 'off'
  /** This device is registered and will be told when food needs using. */
  | 'on';

export interface ReminderEnvironment {
  supported: boolean;
  permission: NotificationPermission;
  subscribed: boolean;
}

/** What to show, given what the browser says. Pure, so the decision is testable. */
export function reminderState(environment: ReminderEnvironment): ReminderState {
  if (!environment.supported) {
    return 'unsupported';
  }
  if (environment.permission === 'denied') {
    return 'blocked';
  }

  return environment.subscribed ? 'on' : 'off';
}

export async function currentState(): Promise<ReminderState> {
  if (!supported()) {
    return 'unsupported';
  }

  const registration = await navigator.serviceWorker.ready;
  const existing = await registration.pushManager.getSubscription();

  return reminderState({
    supported: true,
    permission: Notification.permission,
    subscribed: existing !== null,
  });
}

/**
 * Turns reminders on for this device.
 *
 * The subscription is registered with the server before this reports success: a device the
 * browser knows about but the server does not is a device that will never be told anything,
 * and would look enabled while doing nothing.
 */
export async function enableReminders(client: ApiClient): Promise<ReminderState> {
  if (!supported()) {
    return 'unsupported';
  }

  if ((await Notification.requestPermission()) !== 'granted') {
    return reminderState({
      supported: true,
      permission: Notification.permission,
      subscribed: false,
    });
  }

  const registration = await navigator.serviceWorker.ready;
  const subscription =
    (await registration.pushManager.getSubscription()) ??
    (await registration.pushManager.subscribe({
      // Without this the browser refuses; a payload nobody can read is no use to anyone.
      userVisibleOnly: true,
      applicationServerKey: decodeKey(VAPID_PUBLIC_KEY),
    }));

  await client.subscribeToReminders(subscription.toJSON());

  return 'on';
}

/**
 * Turns them off.
 *
 * The server is told first. If the browser unsubscribed first and the network then failed, the
 * server would keep an endpoint that no longer exists and go on trying it every morning.
 */
export async function disableReminders(client: ApiClient): Promise<ReminderState> {
  if (!supported()) {
    return 'unsupported';
  }

  const registration = await navigator.serviceWorker.ready;
  const existing = await registration.pushManager.getSubscription();
  if (existing === null) {
    return 'off';
  }

  await client.stopReminders(existing.endpoint);
  await existing.unsubscribe();

  return 'off';
}

/**
 * Whether reminders can happen here at all.
 *
 * The VAPID key belongs in this answer and was missing from it, which is a nastier bug than it
 * sounds: `enableReminders` refused without a key while `currentState` went on reporting `off`,
 * so the invitation was offered on every screen, did nothing when pressed, and came back the
 * moment the next screen mounted. One predicate, asked by both.
 */
function supported(): boolean {
  return (
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window &&
    VAPID_PUBLIC_KEY !== ''
  );
}

/**
 * Turns the base64url key into the bytes `applicationServerKey` wants.
 *
 * `atob` speaks standard base64 with padding, and this is neither.
 */
function decodeKey(key: string): Uint8Array<ArrayBuffer> {
  const padded = key.padEnd(key.length + ((4 - (key.length % 4)) % 4), '=');
  const standard = padded.replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(standard);

  // Allocated over a plain ArrayBuffer rather than built with `from`, which leaves the buffer
  // type open and so does not satisfy `applicationServerKey`.
  const bytes = new Uint8Array(new ArrayBuffer(raw.length));
  for (let at = 0; at < raw.length; at += 1) {
    bytes[at] = raw.charCodeAt(at);
  }

  return bytes;
}
